import axios, { Canceler } from 'axios';
import { sortBy } from 'lodash';
import React from 'react';

import { Link } from '@dndbeyond/character-common-components/es';
import { LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    ApiAdapterUtils,
    BaseSpellContract,
    CharacterTheme,
    DataOriginRefData,
    EntityValueLookup,
    FormatUtils,
    OverallSpellInfo,
    RuleData,
    RuleDataUtils,
    Spell,
    SpellCasterInfo,
    SpellUtils,
} from '@dndbeyond/character-rules-engine/es';

import DataLoadingStatusEnum from '../../constants/DataLoadingStatusEnum';
import { ApiSpellsPromise, ApiSpellsRequest } from '../../selectors/composite/apiCreator';
import { AppLoggerUtils, FilterUtils } from '../../utils';
import { ThemeButton } from '../common/Button';
import SpellManagerItem from './SpellManagerItem';

enum LoadSpellType {
    ADDITIONAL = 'ADDITIONAL',
    ALWAYS_KNOWN = 'ALWAYS_KNOWN',
}
interface Props {
    spells: Array<Spell>;
    loadSpells?: ApiSpellsRequest | null;
    loadAlwaysKnownSpells?: ApiSpellsRequest | null;
    knownSpellIds: Array<string>;
    isPrepareMaxed: boolean;
    isCantripsKnownMaxed: boolean;
    isSpellsKnownMaxed: boolean;
    addButtonText: string;
    enableAdd: boolean;
    enablePrepare: boolean;
    enableUnprepare: boolean;
    showFilters: boolean;
    buttonActiveStyle?: string;
    buttonUnprepareText?: string;
    enableSpellRemove: boolean;
    showExpandedType: boolean;
    showCustomize: boolean;
    onAlwaysKnownLoad?: (spells: Array<BaseSpellContract>) => void;
    onPrepare: (spell: Spell) => void;
    onUnprepare: (spell: Spell) => void;
    onRemove: (spell: Spell) => void;
    onAdd: (spell: Spell) => void;
    entityValueLookup: EntityValueLookup;
    overallSpellInfo: OverallSpellInfo;
    spellCasterInfo: SpellCasterInfo;
    ruleData: RuleData;
    dataOriginRefData: DataOriginRefData;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    query: string;
    lazySpells: Array<Spell>;
    staticSpells: Array<Spell>;
    filteredSpells: Array<Spell>;
    currentPage: number;
    filterLevels: Array<number>;
    filterQuery: string;
    loadingStatus: DataLoadingStatusEnum;
}
export default class SpellManager extends React.PureComponent<Props, State> {
    static defaultProps = {
        showFilters: true,
        enableAdd: true,
        enableSpellRemove: true,
        showExpandedType: true,
        enablePrepare: true,
        enableUnprepare: true,
    };

    loadRuleCancelers: Array<Canceler> = [];

    constructor(props: Props) {
        super(props);

        this.state = {
            query: '',
            lazySpells: [],
            staticSpells: props.spells,
            filteredSpells: props.spells,
            currentPage: 0,
            loadingStatus: DataLoadingStatusEnum.NOT_INITIALIZED,
            filterLevels: [],
            filterQuery: '',
        };
    }

    componentDidMount() {
        const { loadSpells, loadAlwaysKnownSpells, onAlwaysKnownLoad } = this.props;

        if (!loadSpells && !loadAlwaysKnownSpells) {
            this.setState({
                loadingStatus: DataLoadingStatusEnum.LOADED,
            });
            return;
        }

        let requests: Array<ApiSpellsPromise> = [];
        let loadingRequestTypes: Array<LoadSpellType> = [];
        if (loadSpells) {
            requests.push(
                loadSpells({
                    cancelToken: new axios.CancelToken((c) => {
                        this.loadRuleCancelers.push(c);
                    }),
                }),
            );
            loadingRequestTypes.push(LoadSpellType.ADDITIONAL);
        }
        if (loadAlwaysKnownSpells) {
            requests.push(
                loadAlwaysKnownSpells({
                    cancelToken: new axios.CancelToken((c) => {
                        this.loadRuleCancelers.push(c);
                    }),
                }),
            );
            loadingRequestTypes.push(LoadSpellType.ALWAYS_KNOWN);
        }

        this.setState({
            loadingStatus: DataLoadingStatusEnum.LOADING,
        });

        axios
            .all(requests)
            .then((responses) => {
                let lazySpells: Array<Spell> = [];

                for (let i = 0; i < responses.length; i++) {
                    let response = responses[i];
                    let requestType = loadingRequestTypes[i];
                    let spells = ApiAdapterUtils.getResponseData(response);
                    switch (requestType) {
                        case LoadSpellType.ADDITIONAL:
                            lazySpells = spells !== null ? this.transformLoadedSpells(spells) : [];
                            break;

                        case LoadSpellType.ALWAYS_KNOWN:
                            if (spells !== null && onAlwaysKnownLoad) {
                                onAlwaysKnownLoad(spells);
                            }
                            break;

                        default:
                        // not implemented
                    }
                }

                this.setState({
                    lazySpells,
                    loadingStatus: DataLoadingStatusEnum.LOADED,
                });
                this.loadRuleCancelers = [];
            })
            .catch(AppLoggerUtils.handleAdhocApiError);
    }

    componentWillUnmount(): void {
        if (this.loadRuleCancelers.length > 0) {
            this.loadRuleCancelers.forEach((cancel) => {
                cancel();
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { spells } = this.props;

        if (spells !== prevProps.spells) {
            this.setState({
                staticSpells: spells,
            });
        }
    }

    getCombinedSpells = (): Array<Spell> => {
        const { lazySpells, staticSpells } = this.state;
        const { knownSpellIds } = this.props;

        const remainingLazySpells = lazySpells.filter(
            (spell) => !knownSpellIds.includes(SpellUtils.deriveKnownKey(spell)),
        );

        return sortBy(
            [...remainingLazySpells, ...staticSpells],
            [
                (spell) => SpellUtils.getLevel(spell),
                (spell) => SpellUtils.getName(spell),
                (spell) => SpellUtils.getExpandedDataOriginRef(spell) !== null,
                (spell) => SpellUtils.getUniqueKey(spell),
            ],
        );
    };

    getFilteredSpells = (combinedSpells: Array<Spell>): Array<Spell> => {
        const { filterLevels, filterQuery } = this.state;

        return combinedSpells.filter((spell) => {
            if (filterLevels.length !== 0 && !filterLevels.includes(SpellUtils.getLevel(spell))) {
                return false;
            }

            if (filterQuery !== '' && !FilterUtils.doesQueryMatchData(filterQuery, SpellUtils.getName(spell))) {
                return false;
            }

            return true;
        });
    };

    transformLoadedSpells = (data: Array<BaseSpellContract>): Array<Spell> => {
        const { ruleData, overallSpellInfo } = this.props;
        return data.map((definition) => SpellUtils.simulateSpell(definition, overallSpellInfo, ruleData, null, null));
    };

    handleFilterSpellLevel = (level: number): void => {
        this.setState((prevState) => ({
            filterLevels: prevState.filterLevels.includes(level)
                ? prevState.filterLevels.filter((l) => l !== level)
                : [...prevState.filterLevels, level],
        }));
    };

    handleQueryChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            filterQuery: evt.target.value,
        });
    };

    renderSpellFilterUi = (combinedSpells: Array<Spell>): React.ReactNode => {
        const { filterQuery, filterLevels } = this.state;
        const { ruleData } = this.props;

        let activeSpellLevelCounts: Array<number> = [];
        let spellLevels: Array<number> = [];
        const maxSpellLevel = RuleDataUtils.getMaxSpellLevel(ruleData);
        for (let i = 0; i <= maxSpellLevel; i++) {
            activeSpellLevelCounts.push(0);
            spellLevels.push(i);
        }

        combinedSpells.forEach((spell) => {
            activeSpellLevelCounts[SpellUtils.getLevel(spell)] += 1;
        });

        return (
            <div className="ct-spell-manager__filters">
                <div className="ct-spell-manager__filter">
                    <div className="ct-spell-manager__filter-heading">Filter</div>
                    <input
                        type="search"
                        className="ct-filter__query"
                        value={filterQuery}
                        onChange={this.handleQueryChange}
                        placeholder={'Enter Spell Name'}
                        spellCheck={false}
                        autoComplete={'off'}
                    />
                </div>
                <div className="ct-spell-manager__filter">
                    <div className="ct-spell-manager__filter-heading">Filter By Spell Level</div>
                    <div className="ct-spell-manager__filter-levels">
                        {spellLevels.map((level) => {
                            if (activeSpellLevelCounts[level] === 0) {
                                return null;
                            }

                            let buttonLabel: React.ReactNode;
                            if (level === 0) {
                                buttonLabel = FormatUtils.renderSpellLevelAbbreviation(0);
                            } else {
                                buttonLabel = (
                                    <div className="ct-spell-manager__filter-level-text">
                                        <span className="ct-spell-manager__filter-level-number">{level}</span>
                                        <span className="ct-spell-manager__filter-level-ordinal">
                                            {FormatUtils.getOrdinalSuffix(level)}
                                        </span>
                                    </div>
                                );
                            }

                            return (
                                <div className="ct-spell-manager__filter-level" key={level}>
                                    <ThemeButton
                                        style={filterLevels.includes(level) ? '' : 'outline'}
                                        size="small"
                                        onClick={this.handleFilterSpellLevel.bind(this, level)}
                                    >
                                        {buttonLabel}
                                    </ThemeButton>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    renderUi = (): React.ReactNode => {
        const {
            onPrepare,
            onUnprepare,
            onRemove,
            onAdd,
            isPrepareMaxed,
            isCantripsKnownMaxed,
            isSpellsKnownMaxed,
            addButtonText,
            enableAdd,
            enablePrepare,
            enableUnprepare,
            enableSpellRemove,
            spellCasterInfo,
            ruleData,
            showFilters,
            buttonActiveStyle,
            buttonUnprepareText,
            entityValueLookup,
            dataOriginRefData,
            showExpandedType,
            showCustomize,
            proficiencyBonus,
            theme,
        } = this.props;
        const combinedSpells = this.getCombinedSpells();
        const filteredSpells = this.getFilteredSpells(combinedSpells);

        return (
            <React.Fragment>
                {showFilters && (
                    <React.Fragment>
                        {this.renderSpellFilterUi(combinedSpells)}
                        <div className="ct-character-tools__marketplace-callout">
                            Looking for something not in the list below? Unlock all official options in the{' '}
                            <Link url="/marketplace">Marketplace</Link>.
                        </div>
                    </React.Fragment>
                )}
                {filteredSpells.map((spell, idx) => (
                    <SpellManagerItem
                        theme={theme}
                        spell={spell}
                        key={`${spell.id}-${idx}`}
                        onPrepare={onPrepare}
                        onUnprepare={onUnprepare}
                        onRemove={onRemove}
                        onAdd={onAdd}
                        isPrepareMaxed={isPrepareMaxed}
                        isCantripsKnownMaxed={isCantripsKnownMaxed}
                        isSpellsKnownMaxed={isSpellsKnownMaxed}
                        addButtonText={addButtonText}
                        enableAdd={enableAdd}
                        enablePrepare={enablePrepare}
                        enableUnprepare={enableUnprepare}
                        enableSpellRemove={enableSpellRemove}
                        buttonUnprepareText={buttonUnprepareText}
                        buttonActiveStyle={buttonActiveStyle}
                        spellCasterInfo={spellCasterInfo}
                        ruleData={ruleData}
                        entityValueLookup={entityValueLookup}
                        showExpandedType={showExpandedType}
                        showCustomize={showCustomize}
                        dataOriginRefData={dataOriginRefData}
                        proficiencyBonus={proficiencyBonus}
                    />
                ))}
            </React.Fragment>
        );
    };

    renderLoading = (): React.ReactNode => {
        return <LoadingPlaceholder />;
    };

    render() {
        const { loadingStatus } = this.state;

        let content: React.ReactNode;
        switch (loadingStatus) {
            case DataLoadingStatusEnum.LOADED:
                content = this.renderUi();
                break;

            case DataLoadingStatusEnum.LOADING:
            default:
                content = this.renderLoading();
                break;
        }

        return <div className="ct-spell-manager">{content}</div>;
    }
}
